import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StepWrapper } from '../../../../../components/styled/StepWrapper';
import { ActionButtons } from '../../../ActionButtons/ActionButtons';
import { SelectedAppointment } from './SelectedAppointment/SelectedAppointment';
import { AppointmentDateSelector } from '../../../../../components/bookingDateTime/AppointmentDateSelector/AppointmentDateSelector';
import { AppointmentTimeSelector } from '../../../../../components/bookingDateTime/AppointmentTimeSelector/AppointmentTimeSelector';
import { useHistory, useParams } from 'react-router-dom';
import { decodeSCID, getCategories, mapRecallsForRequest } from '../../../../../utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../store/rootReducer';
import {
  EAppointmentTimingType,
  IAppointmentSlotsRequest,
  MPOptionShort,
} from '../../../../../store/reducers/appointment/types';
import {
  getServiceValetSlots,
  loadAllServiceCenterSettings,
  loadAppointmentSlots,
  loadServiceValetSlots,
  selectAppointment,
  selectServiceValetAppointment,
} from '../../../../../store/reducers/appointment/actions';
import { TGroupedAppointments } from '../../../../../utils/types';
import ReactGA from 'react-ga4';
import {
  EServiceType,
  EUserType,
} from '../../../../../store/reducers/appointmentFrameReducer/types';
import { TArgCallback, TCallback, TParsableDate } from '../../../../../types/types';
import { TScreen } from '../../../../../types/screens';
import { SVAppointmentDateSelector } from '../../../../../components/bookingDateTime/SVAppointmentDateSelector/SVAppointmentDateSelector';
import { SVAppointmentTimeSelector } from '../../../../../components/bookingDateTime/SVAppointmentTimeSelector/SVAppointmentTimeSelector';
import {
  clearAppointmentSteps,
  loadActiveTransportations,
  loadConsultants,
  searchForCustomerConsents,
  setServiceTypeOption,
  setTime,
  setWelcomeScreenView,
} from '../../../../../store/reducers/appointmentFrameReducer/actions';
import { useTranslation } from 'react-i18next';
import { SlotsScreenWrapper } from './styles';
import { groupAppointments, toUtcWallClockIso } from './utils';
import { Routes } from '../../../../../routes/constants';
import dayjs from 'dayjs';
import CustomerConsents from '../../../../../components/modals/booking/CustomerConsents/CustomerConsents';
import { useModal } from '../../../../../hooks/useModal/useModal';
import MileageModal from '../../../../../components/modals/booking/MileageModal/MileageModal';
import { IFirstScreenOption } from '../../../../../store/reducers/serviceTypes/types';
import utc from 'dayjs/plugin/utc';
import { useException } from '../../../../../hooks/useException/useException';
import AppointmentFilters from './AppointmentFilters/AppointmentFilters';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  getClearDate,
  sortAppointments,
  sortSVAppointments,
} from '../../../../../utils/svAppointments';
import {
  collectServiceRequestIds,
  getRecallCategoryId,
  getSelectedCategoriesWithRequests,
} from '../../../../../utils/collectServiceRequestIds';
import { ETransportationType } from '../../../../../store/reducers/transportationNeeds/types';

dayjs.extend(utc);

type TAppointmentSelectionProps = {
  handleSetScreen: TArgCallback<TScreen>;
  onNext: TCallback;
  prevLogicalScreen: TScreen;
  fromServiceValetToVisitCenter?: boolean;
  isManaging?: boolean;
};

const AppointmentSlotsContent: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<TAppointmentSelectionProps>>
> = ({ handleSetScreen, onNext, prevLogicalScreen, fromServiceValetToVisitCenter, isManaging }) => {
  const {
    appointmentSlots,
    serviceValetSlots,
    customerLoadedData,
    selectedSR,
    appointment,
    serviceValetAppointment,
    customerEnteredEmail,
    slotsServiceTypeOptionId,
    slotsTransportationId,
    slotsSearchedDate,
    selectedSRComments,
    isCloneMode,
    serviceValetCapacity,
    isDemandSmoothMode,
  } = useSelector((state: RootState) => state.appointment);
  const { firstScreenOptions } = useSelector(({ serviceTypes }: RootState) => serviceTypes);

  const {
    selectedTiming,
    selectedTime,
    selectedVehicle,
    service,
    subService,
    selectedPackage,
    advisor,
    serviceCategories,
    valueService,
    userType,
    hashKey,
    zipCode,
    address,
    politicalState,
    city,
    streetName,
    selectedRecalls,
    serviceTypeOption,
    packagePricingType,
    packageEMenuType,
    consultants,
    appointmentByKey,
    isConsultantsLoading,
    isConsentsLoading,
    trackerData,
    transportation,
    editingPosition,
    serviceOptionChangedFromSlotPage,
    transportations,
  } = useSelector((state: RootState) => state.appointmentFrame);
  const { currentConfig, isAppointmentTimingAvailable, isTransportationAvailable } = useSelector(
    (state: RootState) => state.bookingFlowConfig
  );

  const { allCategories } = useSelector((state: RootState) => state.categories);
  const { mileage } = useSelector((state: RootState) => state.vehicleDetails);

  const [firstDayWithSlots, setFirstDayWithSlots] = useState<TParsableDate | null>(null);
  const [date, setDate] = useState<TParsableDate>(dayjs.utc().startOf('day'));
  const [month, setMonth] = useState<TParsableDate>(dayjs.utc());
  const [loading, setLoading] = useState<boolean>(false);
  const dateSlotsRef = useRef<HTMLDivElement | null>(null);
  const [currentApiStartDate, setCurrentApiStartDate] = useState<string | null>(null);
  const [currentApiEndDate, setCurrentApiEndDate] = useState<string | null>(null);
  const apiDatesSetRef = useRef<boolean>(false);
  const initialSlotsRangeRef = useRef<{ start: string | null; end: string | null }>({
    start: null,
    end: null,
  });
  const { id } = useParams<{ id: string }>();
  const initRef = useRef<boolean>(false);
  const isMount = useRef(true);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const showError = useException();
  const { isOpen: isMileageOpen, onClose: onMileageClose, onOpen: onMileageOpen } = useModal();
  const {
    isOpen: isServiceOptionOpen,
    onClose: onServiceOptionClose,
    onOpen: onServiceOptionOpen,
  } = useModal();
  const theme = useTheme();

  const serviceType = useMemo(() => {
    if (serviceTypeOption) {
      return serviceTypeOption.type;
    }

    return transportation?.type === ETransportationType.PickUpDelivery
      ? EServiceType.PickUpDropOff
      : EServiceType.VisitCenter;
  }, [serviceTypeOption, transportation]);

  const nextDisabled = useMemo(
    () => (serviceType === EServiceType.PickUpDropOff ? !serviceValetAppointment : !appointment),
    [appointment, serviceValetAppointment]
  );

  const isMd = useMediaQuery(theme.breakpoints.down('md'));
  const isXs = useMediaQuery(theme.breakpoints.down('xsm'));
  const isMds = useMediaQuery(theme.breakpoints.down('mds'));
  const daysPerScreen: number = useMemo(() => {
    return isXs ? 3 : isMd ? 4 : isMds ? 5 : 6;
  }, [isMd, isMds, isXs]);

  const groupedAppointments: TGroupedAppointments = useMemo(() => {
    return groupAppointments(appointmentSlots);
  }, [appointmentSlots]);

  const currentSlots = useMemo(
    () => (serviceType === EServiceType.PickUpDropOff ? serviceValetSlots : appointmentSlots),
    [serviceTypeOption, serviceValetSlots, appointmentSlots]
  );

  const currentAppointment = useMemo(() => {
    return serviceType === EServiceType.PickUpDropOff ? serviceValetAppointment : appointment;
  }, [serviceTypeOption, serviceValetAppointment, appointment]);

  const handleGALandingOnPage = useCallback(() => {
    if (consultants?.length && currentConfig?.advisorSelection) {
      ReactGA.event('advisor_selected', {
        advisor_name: advisor ? advisor.name : 'Any available',
      });
    }
    if (appointment) {
      ReactGA.event('services_selected', {
        request_codes: appointment?.serviceRequestPrices?.map(item => item.requestName).join(', '),
        total_price: !isNaN(appointment?.price?.value) ? `$${+appointment.price.value}` : undefined,
      });
    }
  }, [advisor, appointment, consultants, currentConfig, trackerData]);

  useEffect(() => {
    handleGALandingOnPage();
  }, [selectedPackage, advisor, appointment]);

  useEffect(() => {
    if (selectedTime) setMonth(dayjs.utc(selectedTime));
  }, [selectedTime]);

  const selectFirstSlot = useCallback(
    (
      date?: TParsableDate,
      newServiceOption?: IFirstScreenOption,
      haveToOffsetConverter?: boolean
    ) => {
      const serviceOption = newServiceOption ?? serviceTypeOption;
      const currentSlots =
        serviceOption?.type === EServiceType.PickUpDropOff ||
        transportation?.type === ETransportationType.PickUpDelivery
          ? serviceValetSlots
          : appointmentSlots;
      if (currentSlots?.length) {
        const utcOffset = dayjs().utcOffset();
        const newDate = date ?? dayjs();

        // added a hook with Math.abs, and a flag that we only give in "Choose a preferred date" when the time zones differ by more than eight hours
        const dateWithOffset = dayjs(newDate).isSame(dayjs(), 'date')
          ? dayjs()
          : (haveToOffsetConverter ? Math.abs(utcOffset) : utcOffset) > 0
            ? dayjs(newDate)
            : getClearDate(newDate);

        let firstAvailableSlot;
        if (
          serviceOption?.type === EServiceType.PickUpDropOff ||
          transportation?.type === ETransportationType.PickUpDelivery
        ) {
          const sorted = [...serviceValetSlots].sort(sortSVAppointments);
          firstAvailableSlot = sorted.find(slot => {
            const formatted = dayjs(slot?.date).add(Math.abs(utcOffset), 'minutes');
            return (
              formatted.isAfter(getClearDate(newDate)) ||
              formatted.isSame(getClearDate(newDate), 'day')
            );
          });
          if (!firstAvailableSlot) {
            console.info('Can not assign first available slot for pickUpDropOff');
          } else {
            const dayName = dayjs(firstAvailableSlot.date).format('dddd');
            if (serviceValetCapacity) {
              const capacityForDay = serviceValetCapacity[dayName];
              const firstASlotWithData = {
                ...firstAvailableSlot,
                pickUpMin: capacityForDay.pickUpMin || '',
                pickUpMax: capacityForDay.pickUpMax || '',
                dropOffMin: capacityForDay.dropOffMin || '',
                dropOffMax: capacityForDay.dropOffMax || '',
              };
              dispatch(selectServiceValetAppointment(firstASlotWithData));

              setDate(dayjs.utc(firstASlotWithData.date).startOf('day'));
            } else {
              showError('There was an error loading capacity data. Please try again later.');
            }
          }
        } else {
          const sorted = [...appointmentSlots].sort(sortAppointments);
          firstAvailableSlot = sorted.find(slot => {
            const formatted = getClearDate(slot?.date);
            return dayjs(formatted).isAfter(dateWithOffset);
          });
          if (!firstAvailableSlot) {
            console.info('Can not assign first available slot for general');
          } else {
            dispatch(selectAppointment(firstAvailableSlot));
            setDate(firstAvailableSlot.date);
          }
        }
      }
    },
    [serviceValetSlots, appointmentSlots, currentSlots]
  );

  useEffect(() => {
    if (currentSlots.length) {
      const utcOffset = dayjs().utcOffset();
      const dateWithOffset = dayjs(slotsSearchedDate as TParsableDate).isSame(dayjs(), 'date')
        ? dayjs()
        : utcOffset > 0
          ? dayjs(slotsSearchedDate as TParsableDate)
          : getClearDate(slotsSearchedDate as TParsableDate);
      if (currentAppointment?.date) {
        const sameSearchDate = getClearDate(currentAppointment.searchDate).isSame(
          dateWithOffset,
          'date'
        );
        const slotTimeIsValid = dayjs(getClearDate(currentAppointment.date)).isAfter(
          dateWithOffset
        );
        const theSameServiceOption = slotsServiceTypeOptionId === serviceTypeOption?.id;
        const theSameTransportation = slotsTransportationId === transportation?.id;
        if (theSameServiceOption && sameSearchDate && slotTimeIsValid) {
          if (theSameTransportation) {
            if (selectedTime) {
              selectFirstSlot(dayjs(selectedTime).isSame(dayjs(), 'date') ? dayjs() : selectedTime);
            } else {
              selectFirstSlot();
            }
          } else {
            setDate(dayjs.utc(currentAppointment.date).startOf('day'));
            if (serviceType === EServiceType.PickUpDropOff) selectFirstSlot();
          }
        } else {
          if (selectedTime) {
            selectFirstSlot(
              dayjs(selectedTime).isSame(dayjs(), 'date') ? dayjs() : selectedTime,
              undefined,
              true
            );
          } else {
            selectFirstSlot();
          }
        }
      } else {
        if (selectedTime) {
          selectFirstSlot(
            dayjs(selectedTime).isSame(dayjs(), 'date') ? dayjs() : selectedTime,
            undefined,
            true
          );
        } else {
          selectFirstSlot();
        }
      }
      isMount.current = false;
    }
  }, [
    selectedTime,
    selectFirstSlot,
    currentSlots,
    serviceTypeOption,
    slotsServiceTypeOptionId,
    slotsSearchedDate,
    slotsTransportationId,
    transportation,
  ]);

  useEffect(() => {
    let timeoutId: any = null;
    const isTodaySlot = dayjs(appointment?.date).isSame(dayjs.utc(), 'day');
    const differenceInMSeconds = dayjs(dayjs(appointment?.date).format('YYYY-MM-DDTHH:mm:ss')).diff(
      dayjs.utc()
    );
    if (isTodaySlot && differenceInMSeconds > 0) {
      timeoutId = setTimeout(() => {
        selectFirstSlot(date);
      }, differenceInMSeconds);
    } else {
      clearTimeout(timeoutId);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [appointment, date]);

  useEffect(() => {
    const shouldLoadConsultants = Boolean(currentConfig?.advisorSelection);
    if (shouldLoadConsultants) {
      dispatch(loadConsultants(id, serviceTypeOption?.id ?? null));
    }
  }, [currentConfig?.advisorSelection, id, serviceTypeOption?.id]);

  const clearData = () => {
    dispatch(selectAppointment(null));
    dispatch(selectServiceValetAppointment(null));
    dispatch(
      clearAppointmentSteps(
        isTransportationAvailable ? 'transportationNeeds' : 'appointmentSelection'
      )
    );
  };

  const updateDate = useCallback(
    (d: TParsableDate, keepSlot?: boolean) => {
      if (!keepSlot) {
        clearData();
      }

      const newDate = dayjs.utc(d);
      const minDate = newDate.isSame(dayjs.utc(), 'date') ? dayjs.utc() : newDate;
      setDate(newDate);

      if (!keepSlot) {
        selectFirstSlot(minDate);
      }

      if (!newDate.isSame(dayjs.utc(month), 'month')) {
        setMonth(newDate);
      }
    },
    [month, selectedTiming, selectFirstSlot]
  );

  const setDateCallback = useCallback(
    (d: TParsableDate) => {
      if (selectedTiming !== EAppointmentTimingType.FirstAvailable) {
        setDate(dayjs(d).startOf('day'));
      }
    },
    [selectedTiming]
  );

  const handleDateRangeSet = useCallback((v: boolean) => {
    initRef.current = v;
  }, []);

  const handleError = (e: any) => {
    const internalServerError = e.response?.data?.message
      ?.toLowerCase()
      .includes('internal server');
    if (internalServerError) {
      const errorMessage = `We are sorry but there is a capacity configuration error. 
            No appointment dates and times are available for the specific appointment request. 
            Error identifier: ${e.response?.data?.id ?? ''}`;
      showError(errorMessage);
    } else {
      showError(e);
    }
  };

  const onLoadSlots = (
    isEmptyList: boolean,
    requestedStartDate?: string,
    requestedEndDate?: string
  ) => {
    const isInitialRange =
      !!requestedStartDate &&
      !!requestedEndDate &&
      initialSlotsRangeRef.current.start === requestedStartDate &&
      initialSlotsRangeRef.current.end === requestedEndDate;
    const isPossibleToChangeType =
      firstScreenOptions.filter(item => item.type !== EServiceType.MobileService)?.length > 1;
    const isMobileServiceType = serviceType === EServiceType.MobileService;
    if (isEmptyList && isPossibleToChangeType && !isMobileServiceType && isInitialRange) {
      onServiceOptionOpen();
    }
  };

  const getApiDates = () => {
    const anchorTime = selectedTime ? dayjs(selectedTime) : dayjs().startOf('day');
    const idealStartDay = isDemandSmoothMode
      ? dayjs(appointmentByKey?.dateInUtc)
      : anchorTime.subtract(Math.floor(daysPerScreen / 3), 'day').startOf('day');
    const desiredStartDate = dayjs.max(dayjs().startOf('day'), idealStartDay);
    const desiredEndDate = desiredStartDate.add(daysPerScreen - 1, 'day');
    const apiStartDate = toUtcWallClockIso(desiredStartDate);
    const apiEndDate = toUtcWallClockIso(desiredEndDate);
    return { apiStartDate, apiEndDate };
  };

  const setApiDates = (newStartDate: string, isPickUpDropOff: boolean) => {
    // Only run once per session/component mount
    if (apiDatesSetRef.current) {
      return;
    }

    apiDatesSetRef.current = true;

    const anchorTime = dayjs(newStartDate);
    const idealStartDay = isPickUpDropOff
      ? anchorTime
      : isDemandSmoothMode
        ? dayjs(appointmentByKey?.dateInUtc)
        : anchorTime.subtract(Math.floor(daysPerScreen / 3), 'day').startOf('day');
    const desiredStartDate = dayjs.max(dayjs().startOf('day'), idealStartDay);
    const desiredEndDate = desiredStartDate.add(daysPerScreen - 1, 'day');
    const apiStartDate = toUtcWallClockIso(desiredStartDate);
    const apiEndDate = toUtcWallClockIso(desiredEndDate);
    if (!firstDayWithSlots) setFirstDayWithSlots(apiStartDate);
    setCurrentApiStartDate(apiStartDate);
    setCurrentApiEndDate(apiEndDate);
  };

  const loadData = async ({
    requestedStartDate,
    requestedEndDate,
  }: {
    requestedStartDate?: string;
    requestedEndDate?: string;
  }) => {
    if (!initialSlotsRangeRef.current.start && requestedStartDate && requestedEndDate) {
      initialSlotsRangeRef.current = {
        start: requestedStartDate,
        end: requestedEndDate,
      };
    }

    if (id) {
      if (!firstDayWithSlots && currentApiStartDate) {
        setFirstDayWithSlots(currentApiStartDate);
      } else {
        if (!firstDayWithSlots && serviceType === EServiceType.PickUpDropOff) {
          setFirstDayWithSlots(requestedStartDate);
        }
      }
      setCurrentApiStartDate(requestedStartDate ?? null);
      setCurrentApiEndDate(requestedEndDate ?? null);
      setLoading(true);
      try {
        const maintenancePackageOption: MPOptionShort | null = selectedPackage
          ? { id: selectedPackage?.id, priceType: packagePricingType }
          : packageEMenuType !== null
            ? { optionType: packageEMenuType }
            : null;

        const transportationOptionId =
          serviceType === EServiceType.VisitCenter
            ? serviceTypeOption?.transportationOption
              ? serviceTypeOption?.transportationOption?.id
              : !serviceTypeOption?.transportationOption && transportation
                ? transportation?.id
                : null
            : serviceType === EServiceType.PickUpDropOff
              ? (serviceTypeOption?.transportationOption?.id ?? null)
              : null;

        const isServiceValetExist = firstScreenOptions.some(
          s => s.type === EServiceType.PickUpDropOff
        );
        const isPickDropOff =
          serviceTypeOption?.type === EServiceType.PickUpDropOff ||
          transportation?.type === ETransportationType.PickUpDelivery;
        const optionId: number | null = serviceTypeOption?.id ?? null;

        const pickUpDropOffTransportation =
          transportations.find(t => t.type === ETransportationType.PickUpDelivery)?.id ??
          transportation?.id ??
          null;

        const data: IAppointmentSlotsRequest = {
          appointmentTimingType:
            serviceType === EServiceType.PickUpDropOff || !selectedTiming
              ? EAppointmentTimingType.FirstAvailable
              : selectedTiming,
          serviceCenterId: decodeSCID(id),
          advisorId: advisor?.id ?? null,
          startDate: requestedStartDate,
          endDate: requestedEndDate,
          maintenancePackageOption,
          serviceRequests: collectServiceRequestIds(
            service,
            subService,
            selectedPackage,
            selectedSR,
            undefined,
            selectedSRComments,
            getSelectedCategoriesWithRequests(allCategories, serviceCategories)
          ),
          serviceCategories: getCategories(allCategories, serviceCategories),
          customerId: customerLoadedData?.id,
          warrantyExpiration: selectedVehicle?.warrantyExpiration,
          serviceTypeOptionId: isServiceValetExist ? optionId : isPickDropOff ? null : optionId,
          recalls: mapRecallsForRequest(
            selectedRecalls,
            getRecallCategoryId(service, subService, allCategories, serviceCategories)
          ),
          transportationOptionId: isServiceValetExist
            ? transportationOptionId
            : isPickDropOff
              ? pickUpDropOffTransportation
              : transportationOptionId,
        };
        if (address) {
          data.address = {
            state: politicalState,
            city,
            address: streetName,
            zipCode: zipCode,
          };
        }
        if (selectedVehicle) {
          data.vehicle = {
            id: selectedVehicle.id ? +selectedVehicle.id : undefined,
            dmsId: selectedVehicle.dmsId,
            vin: selectedVehicle.vin,
            year: selectedVehicle.year,
            make: selectedVehicle.make,
            model: selectedVehicle.model,
            mileage: selectedVehicle.mileage,
            engineTypeId: selectedVehicle.engineTypeId,
          };
        }
        if (hashKey) data.appointmentHashKey = hashKey;
        if (userType === EUserType.Existing && customerEnteredEmail)
          data.searchTerm = customerEnteredEmail;
        if (serviceType === EServiceType.PickUpDropOff) {
          if (data.address)
            await dispatch(
              loadServiceValetSlots(
                data,
                undefined,
                isEmptyList => onLoadSlots(isEmptyList, requestedStartDate, requestedEndDate),
                handleError,
                setApiDates
              )
            );
        } else {
          await dispatch(
            loadAppointmentSlots(
              data,
              currentAppointment ? () => {} : setDateCallback,
              () => handleDateRangeSet(false),
              isEmptyList => onLoadSlots(isEmptyList, requestedStartDate, requestedEndDate),
              handleError,
              setApiDates
            )
          );
        }
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const mileageIsValid =
      selectedVehicle?.mileage &&
      mileage.find(item => item.value.toString() === selectedVehicle?.mileage?.toString());
    if (!mileageIsValid) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        onMileageOpen();
      }, 1000);
    } else {
      const { apiStartDate, apiEndDate } = getApiDates();
      loadData({ requestedStartDate: apiStartDate, requestedEndDate: apiEndDate }).finally();
    }
  }, [
    dispatch,
    selectedVehicle,
    customerLoadedData,
    service,
    packagePricingType,
    packageEMenuType,
    serviceTypeOption,
    subService,
    selectedPackage,
    selectedSR,
    advisor,
    valueService,
    zipCode,
    address,
    mileage,
    transportation,
    selectedSRComments,
  ]);

  useEffect(() => {
    if (
      ![EServiceType.PickUpDropOff, EServiceType.MobileService].includes(
        serviceType as EServiceType
      )
    ) {
      dispatch(loadActiveTransportations(decodeSCID(id)));
    }
    dispatch(loadAllServiceCenterSettings(decodeSCID(id)));
  }, [id]);

  const handleGANext = useCallback(() => {
    if (appointment) {
      const eventName =
        serviceType === EServiceType.PickUpDropOff
          ? 'valet_date_time_selected'
          : 'date_time_selected';

      ReactGA.event(eventName, {
        appointment_datetime: dayjs.utc(appointment.date).format('MM-DD-YYYY hh:mm A'),
      });
    }
  }, [appointment, serviceTypeOption, trackerData]);

  const handleGABack = useCallback(() => {
    ReactGA.event('went_backwards', {
      page_context: 'Selection Date & Time Page',
    });
  }, [trackerData]);

  const handleConsents = () => {
    handleSetScreen('appointmentConfirmation');
  };

  const searchForConsents = () => {
    dispatch(searchForCustomerConsents(handleNext));
  };

  const handleNext = useCallback((): void => {
    handleGANext();
    onNext();
  }, [onNext, handleGANext]);

  const handleBack = useCallback((): void => {
    handleGABack();
    dispatch(setTime(null));
    if (!isManaging) {
      dispatch(selectAppointment(null));
      dispatch(selectServiceValetAppointment(null));
      dispatch(getServiceValetSlots([]));
    }
    if (
      prevLogicalScreen === 'appointmentSelection' ||
      (fromServiceValetToVisitCenter && !isAppointmentTimingAvailable)
    ) {
      dispatch(setServiceTypeOption(appointmentByKey?.serviceTypeOption ?? null));
      dispatch(setWelcomeScreenView('serviceSelect'));
      history.push(Routes.EndUser.Welcome + '/' + id + '?frame=1');
    } else {
      if (editingPosition === 'slot' && serviceOptionChangedFromSlotPage) {
        dispatch(setServiceTypeOption(appointmentByKey?.serviceTypeOption ?? null));
      }
      handleSetScreen(prevLogicalScreen);
    }
  }, [currentConfig, history, fromServiceValetToVisitCenter, prevLogicalScreen, isManaging]);

  const loadDataForMileage = async () => {
    onMileageClose();
    loadNextSlots();
  };

  useEffect(() => {
    if (dateSlotsRef.current) {
      const selectedDateElement = dateSlotsRef.current.querySelector(`[data-date="${date}"]`);
      if (selectedDateElement) {
        selectedDateElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [date]);

  const loadNextSlots = async () => {
    if (!currentApiStartDate || !currentApiEndDate) {
      const { apiStartDate, apiEndDate } = getApiDates();
      await loadData({ requestedStartDate: apiStartDate, requestedEndDate: apiEndDate }).finally();
      return;
    }

    const nextStartDate = dayjs(currentApiStartDate).add(daysPerScreen, 'day');
    const nextEndDate = dayjs(currentApiEndDate).add(daysPerScreen, 'day');

    setDate(nextStartDate.startOf('month').toISOString());
    await loadData({
      requestedStartDate: nextStartDate.toISOString(),
      requestedEndDate: nextEndDate.toISOString(),
    }).finally();
  };

  const loadPreviousSlots = () => {
    if (!currentApiStartDate || !currentApiEndDate) {
      return;
    }

    const todayStart = dayjs().utc().startOf('day');

    let previousStartDate = dayjs(currentApiStartDate).subtract(daysPerScreen, 'day');

    if (previousStartDate.isBefore(todayStart)) {
      previousStartDate = todayStart;
    }

    const utcOffset = dayjs().utcOffset();
    const previousEndDate = previousStartDate.add(daysPerScreen - 1, 'day');

    // Added clearDate converter for user with other time zones
    setDate(
      utcOffset < 0
        ? dayjs(getClearDate(previousStartDate)).toISOString()
        : previousStartDate.toISOString()
    );

    // Added setTime for right set selectedSlot using backward button
    dispatch(
      setTime(utcOffset < 0 ? getClearDate(previousStartDate) : previousStartDate.toISOString())
    );

    loadData({
      requestedStartDate: previousStartDate.toISOString(),
      requestedEndDate: previousEndDate.toISOString(),
    }).finally();

    const currentMonth = dayjs(currentApiStartDate).month();
    const previousMonth = previousStartDate.month();

    if (currentMonth !== previousMonth) {
      if (dayjs(currentApiStartDate).date() <= daysPerScreen) {
        setDate(previousStartDate.endOf('month').toISOString());
      } else {
        setDate(previousStartDate.startOf('month').toISOString());
      }
    }
  };

  return (
    <StepWrapper>
      <SlotsScreenWrapper>
        <SelectedAppointment />
        <ActionButtons
          removeTopMargin
          onBack={handleBack}
          onNext={searchForConsents}
          prevDisabled={(!isAppointmentTimingAvailable && isCloneMode) || isDemandSmoothMode}
          nextDisabled={nextDisabled}
          nextLabel={isCloneMode ? t('Confirm') : t('Next')}
          loading={isConsultantsLoading || isConsentsLoading}
        />
        <AppointmentFilters
          isSm={isMd}
          isServiceOptionOpen={isServiceOptionOpen}
          onServiceOptionClose={onServiceOptionClose}
        />
        {serviceType === EServiceType.PickUpDropOff ? (
          <SVAppointmentDateSelector
            firstDayWithSlots={firstDayWithSlots}
            onDateRangeSet={handleDateRangeSet}
            dateChangeDisabled={selectedTiming !== EAppointmentTimingType.SpecialOffers}
            date={date}
            onDateChange={updateDate}
            dateRangeUpdated={initRef.current}
            daysPerScreen={daysPerScreen}
            onLoadNext={loadNextSlots}
            onLoadPrevious={loadPreviousSlots}
            apiStartDate={currentApiStartDate || undefined}
            apiEndDate={currentApiEndDate || undefined}
          />
        ) : (
          <AppointmentDateSelector
            firstDayWithSlots={firstDayWithSlots}
            dateChangeDisabled={selectedTiming !== EAppointmentTimingType.SpecialOffers}
            appointments={groupedAppointments}
            date={date}
            onDateChange={updateDate}
            daysPerScreen={daysPerScreen}
            onLoadNext={loadNextSlots}
            onLoadPrevious={loadPreviousSlots}
            apiStartDate={currentApiStartDate || undefined}
            apiEndDate={currentApiEndDate || undefined}
          />
        )}
        {serviceType === EServiceType.PickUpDropOff ? (
          <SVAppointmentTimeSelector date={date} loading={loading || isConsentsLoading} />
        ) : (
          <AppointmentTimeSelector
            appointments={
              groupedAppointments[dayjs.utc(date).startOf('day').toISOString().replace('.000', '')]
            }
            date={date}
            loading={loading || isConsentsLoading}
          />
        )}
      </SlotsScreenWrapper>
      <CustomerConsents onNext={isCloneMode ? onNext : handleConsents} />
      <MileageModal open={isMileageOpen} onClose={onMileageClose} onSave={loadDataForMileage} />
    </StepWrapper>
  );
};

// Remounts the slots screen when the address changes (e.g. from EditAddressModal),
// so it behaves exactly like leaving the screen and coming back
export const AppointmentSlots: React.FC<TAppointmentSelectionProps> = props => {
  const { address, zipCode } = useSelector((state: RootState) => state.appointmentFrame);
  const addressKey = `${typeof address === 'string' ? address : (address?.label ?? '')}_${
    zipCode ?? ''
  }`;

  return <AppointmentSlotsContent key={addressKey} {...props} />;
};
